/**
 * Memory Analysis Feature Provider for AI Chat Integration
 * 
 * This provider integrates with the AgenticFeatureRegistry to enable
 * the Memory Analysis AI agent when viewing the Environment Monitor.
 */

import { AgenticFeatureRegistry, AgenticFeatureProvider } from '../ai/chat/AgenticFeatureRegistry';

/**
 * Memory Analysis Feature Provider
 * Handles AI agent lifecycle events for memory analysis
 */
export class MemoryAnalysisFeatureProvider implements AgenticFeatureProvider {
  id = 'memory-analysis-provider';
  name = 'Memory Analysis Agent Integration';

  /**
   * Called when the agent ID changes
   */
  onAgentIdChange(agentId: string): void {
   
  }

  /**
   * Called when conversation is cleared
   */
  onConversationClear(): void {
    console.log('🧠 [Memory Analysis] Conversation cleared');
  }

  /**
   * Called when a prompt is sent
   */
  onPromptSent(prompt: string): void {
    console.log(`🧠 [Memory Analysis] Prompt sent: ${prompt.substring(0, 50)}...`);
  }

  /**
   * Called when AI run completes
   */
  onRunCompleted(messages: any[]): void {
    console.log(`🧠 [Memory Analysis] Run completed with ${messages.length} messages`);
  }

  /**
   * Called when focus changes (e.g., switching to heap analysis view)
   */
  onFocusChange(focusKey: string): void {
    console.log(`🧠 [Memory Analysis] Focus changed to: ${focusKey}`);
  }

  /**
   * Return the preferred agent ID for this feature
   */
  getPreferredAgentId(): string | null {
    return null;
  }

  /**
   * Return initial prompts to suggest when this feature is active
   */
  getInitialPrompts(): string[] {
    return [
      "Analyze current memory usage and identify any issues",
      "Help me investigate a memory leak",
      "What's consuming the most memory in the heap?",
      "Set a baseline and help me track memory growth",
      "Check for detached contexts or potential leaks"
    ];
  }

  /**
   * Return a prompt when focus changes (auto-prompt on focus)
   */
  getPromptForFocus(focusKey: string): string | null {
    if (focusKey === 'heap-analysis') {
      return "I just opened the heap analysis view. Can you analyze the current memory state and identify any potential issues?";
    }
    
    if (focusKey === 'memory-leak-investigation') {
      return "I want to investigate a potential memory leak. Please set a baseline so we can track memory growth.";
    }
    
    if (focusKey === 'high-memory-alert') {
      return "Memory usage is high! Please analyze the heap and provide recommendations for reducing memory consumption.";
    }
    
    return null;
  }
}

// Singleton instance
let memoryAnalysisProviderInstance: MemoryAnalysisFeatureProvider | null = null;

/**
 * Initialize and register the Memory Analysis Feature Provider
 * Call this when the Environment Monitor component mounts
 */
export const initializeMemoryAnalysisProvider = (): void => {
  if (memoryAnalysisProviderInstance) {
    console.log('🧠 [Memory Analysis] Provider already initialized');
    return;
  }
  
  memoryAnalysisProviderInstance = new MemoryAnalysisFeatureProvider();
  AgenticFeatureRegistry.register(memoryAnalysisProviderInstance);
  
  console.log('🧠 [Memory Analysis] Feature Provider registered');
};

/**
 * Cleanup the Memory Analysis Feature Provider
 * Call this when leaving the Environment Monitor
 */
export const cleanupMemoryAnalysisProvider = (): void => {
  if (memoryAnalysisProviderInstance) {
    AgenticFeatureRegistry.unregister(memoryAnalysisProviderInstance.id);
    memoryAnalysisProviderInstance = null;
    console.log('🧠 [Memory Analysis] Feature Provider unregistered');
  }
};

/**
 * Set focus for memory analysis context
 * Can be called when user clicks on specific memory sections
 */
export const setMemoryAnalysisFocus = (focusKey: string): void => {
  AgenticFeatureRegistry.setFocusKey(focusKey);
  console.log(`🧠 [Memory Analysis] Focus set to: ${focusKey}`);
};

/**
 * Clear memory analysis focus
 */
export const clearMemoryAnalysisFocus = (): void => {
  AgenticFeatureRegistry.clearFocusKey();
  console.log('🧠 [Memory Analysis] Focus cleared');
};

// Export for use in components
export default {
  initializeMemoryAnalysisProvider,
  cleanupMemoryAnalysisProvider,
  setMemoryAnalysisFocus,
  clearMemoryAnalysisFocus
};

